import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import { compactDate, compactDateTime, dateDaysFromNow, formatNumber, formatQuantity, locationLabel } from "../../display";
import type { Location, LocationsPayload } from "../../types";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type StoreHubStatus = {
  configured?: boolean;
  message?: string;
  transactions_path?: string;
};

type StoreHubImportRow = {
  import_batch_id?: string;
  source_filename?: string;
  source_type?: string;
  imported_at?: string;
  import_start_date?: string;
  import_end_date?: string;
  transaction_count?: number;
  skipped_count?: number;
};

type DepletionRow = {
  branch_code?: string;
  component_sku?: string;
  component_name?: string;
  unit?: string;
  quantity_depleted?: number;
  depletion_rows?: number;
  high_confidence_rows?: number;
};

export function PosSalesWorkspace({ csrfToken }: { csrfToken: string }) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [status, setStatus] = useState<StoreHubStatus | null>(null);
  const [recentRows, setRecentRows] = useState<StoreHubImportRow[]>([]);
  const [depletionRows, setDepletionRows] = useState<DepletionRow[]>([]);
  const [startDate, setStartDate] = useState(() => dateDaysFromNow(0));
  const [endDate, setEndDate] = useState(() => dateDaysFromNow(0));
  const [locationId, setLocationId] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadSales = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [locationPayload, statusPayload, recentPayload, depletionPayload] = await Promise.all([
        apiGet<LocationsPayload>("/api/locations"),
        apiGet<StoreHubStatus>("/api/storehub/api-status"),
        apiGet<{ rows?: StoreHubImportRow[] }>("/api/storehub/recent"),
        apiGet<{ rows?: DepletionRow[] }>(`/api/storehub/depletion-summary?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`)
      ]);
      setLocations(locationPayload.locations || []);
      setStatus(statusPayload);
      setRecentRows(recentPayload.rows || []);
      setDepletionRows(depletionPayload.rows || []);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "POS & Sales could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, [endDate, startDate]);

  useEffect(() => {
    void loadSales();
  }, [loadSales]);

  const syncStoreHub = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await apiPost<{ imported_rows?: number; skipped_rows?: number; batch_id?: string }>("/api/storehub/sync", {
        start_date: startDate,
        end_date: endDate,
        location_id: locationId
      }, csrfToken);
      setMessage({ variant: "success", title: `StoreHub sync imported ${formatNumber(result.imported_rows || 0)} rows and skipped ${formatNumber(result.skipped_rows || 0)} duplicates.` });
      await loadSales();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "StoreHub sync could not run." });
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async (): Promise<void> => {
    if (!csvFile) {
      setMessage({ variant: "danger", title: "Choose a StoreHub CSV file first." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const result = await apiPost<{ imported_rows?: number; batch_id?: string; ignored_columns?: string[] }>("/api/storehub/import-csv", {
        filename: csvFile.name,
        csv_text: await csvFile.text()
      }, csrfToken);
      setMessage({ variant: "success", title: `Imported ${formatNumber(result.imported_rows || 0)} StoreHub rows from ${csvFile.name}.` });
      setCsvFile(null);
      await loadSales();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "StoreHub CSV could not be imported." });
    } finally {
      setSaving(false);
    }
  };

  const recentColumns: StandardColumn<StoreHubImportRow>[] = useMemo(() => [
    { key: "source_filename", label: "File / Source", value: row => row.source_filename || "", render: row => <><strong>{row.source_filename || "StoreHub API"}</strong><small>{row.source_type || ""}</small></> },
    { key: "imported_at", label: "Imported", value: row => row.imported_at || "", render: row => compactDateTime(row.imported_at) },
    { key: "range", label: "Date Range", value: row => `${row.import_start_date || ""} ${row.import_end_date || ""}`, render: row => [compactDate(row.import_start_date), compactDate(row.import_end_date)].filter(Boolean).join(" to ") },
    { key: "transaction_count", label: "Rows", value: row => row.transaction_count || 0, render: row => formatNumber(row.transaction_count || 0), textCenter: true },
    { key: "skipped_count", label: "Skipped", value: row => row.skipped_count || 0, render: row => formatNumber(row.skipped_count || 0), textCenter: true },
    { key: "import_batch_id", label: "Batch", value: row => row.import_batch_id || "" }
  ], []);

  const depletionColumns: StandardColumn<DepletionRow>[] = useMemo(() => [
    { key: "branch_code", label: "Branch", value: row => row.branch_code || "" },
    { key: "component_sku", label: "SKU", value: row => row.component_sku || "" },
    { key: "component_name", label: "Component", value: row => row.component_name || "" },
    { key: "unit", label: "Unit", value: row => row.unit || "" },
    { key: "quantity_depleted", label: "Quantity Depleted", value: row => row.quantity_depleted || 0, render: row => formatQuantity(row.quantity_depleted), textCenter: true },
    { key: "depletion_rows", label: "Rows", value: row => row.depletion_rows || 0, render: row => formatNumber(row.depletion_rows || 0), textCenter: true },
    { key: "high_confidence_rows", label: "High Confidence", value: row => row.high_confidence_rows || 0, render: row => formatNumber(row.high_confidence_rows || 0), textCenter: true }
  ], []);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-pos-sales-view">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>POS & Sales</h1>
            <p>Import StoreHub sales, review recent batches, and inspect item depletion.</p>
          </FlexItem>
          {loading && <FlexItem><Spinner size="md" aria-label="Loading POS & Sales" /></FlexItem>}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <div className="pf-toj-work-grid">
          <Card className="pf-toj-work-card pf-toj-primary-work-card">
            <CardBody>
              <Flex justifyContent={{ default: "justifyContentSpaceBetween" }} alignItems={{ default: "alignItemsCenter" }}>
                <FlexItem>
                  <h2>StoreHub API Sync</h2>
                  <p className="pf-toj-muted">{status?.configured ? `Ready: ${status.transactions_path || "configured endpoint"}` : status?.message || "StoreHub API status is loading."}</p>
                </FlexItem>
                <FlexItem><Label color={status?.configured ? "green" : "orange"}>{status?.configured ? "Configured" : "Needs setup"}</Label></FlexItem>
              </Flex>
              <Form className="pf-toj-form">
                <div className="pf-toj-field-row">
                  <FormGroup label="Start date"><TextInput type="date" value={startDate} onChange={(_event, value) => setStartDate(String(value))} aria-label="StoreHub sync start date" /></FormGroup>
                  <FormGroup label="End date"><TextInput type="date" value={endDate} onChange={(_event, value) => setEndDate(String(value))} aria-label="StoreHub sync end date" /></FormGroup>
                  <FormGroup label="Location"><FormSelect value={locationId} onChange={(_event, value) => setLocationId(String(value))} aria-label="StoreHub sync location"><FormSelectOption value="" label="All visible locations" />{locations.map(location => <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />)}</FormSelect></FormGroup>
                </div>
                <Button variant="primary" onClick={() => void syncStoreHub()} isLoading={saving} isDisabled={saving || !status?.configured}>Run sync</Button>
              </Form>
            </CardBody>
          </Card>
          <Card className="pf-toj-work-card">
            <CardBody>
              <h2>Import StoreHub CSV</h2>
              <p className="pf-toj-muted">Use the exported StoreHub transaction file when API sync is not available.</p>
              <Form className="pf-toj-form">
                <FormGroup label="StoreHub CSV">
                  <input
                    id="pf-storehub-csv"
                    className="pf-toj-file-input"
                    type="file"
                    accept=".csv,text/csv"
                    aria-label="StoreHub CSV file"
                    onChange={event => setCsvFile(event.currentTarget.files?.[0] || null)}
                  />
                </FormGroup>
                <Button variant="primary" onClick={() => void importCsv()} isLoading={saving} isDisabled={saving || !csvFile}>Import CSV</Button>
              </Form>
            </CardBody>
          </Card>
        </div>
        <Card className="pf-toj-work-card pf-toj-table-card">
          <CardBody>
            <h2>Recent StoreHub Imports</h2>
            <StandardDataTable ariaLabel="Recent StoreHub imports" rows={recentRows} columns={recentColumns} searchPlaceholder="Search recent imports" />
          </CardBody>
        </Card>
        <Card className="pf-toj-work-card pf-toj-table-card">
          <CardBody>
            <h2>Sales Depletion</h2>
            <StandardDataTable ariaLabel="StoreHub depletion rows" rows={depletionRows} columns={depletionColumns} searchPlaceholder="Search depletion rows" />
          </CardBody>
        </Card>
      </div>
    </PageSection>
  );
}
